import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ModalPortal from '../UI/ModalPortal';
import { X, Youtube, Calendar, TrendingUp, ExternalLink } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import statisticsService from '../../services/statisticsService';
import { Loader } from '../UI/LoadingSpinner';
import StatCard from '../Dashboard/StatCard';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Pagination } from '@anyemp/ui-kit';
import StatisticsTable from './StatisticsTable';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ManagerYoutubeDetailsModal = ({ manager, filters, onClose }) => {
  const location = useLocation();

  const youtubeItemRowKey = useCallback(
    (item) => item.id ?? `${item.file_name}-${item.uploaded_at}`,
    []
  );

  const recentYoutubeColumns = useMemo(() => [
    {
      key: 'file_name',
      label: 'File / URL',
      wrap: true,
      render: (item) => (
        <span className="text-sm text-gray-900 dark:text-gray-100 break-all">
          {item.file_name || `Item #${item.id}`}
        </span>
      ),
    },
    {
      key: 'talent',
      label: 'Talent',
      render: (item) => item.talent_id && item.talent_name ? (
        <Link
          to={`/talents/${item.talent_id}`}
          state={{ from: location.pathname + location.search }}
          className="flex items-center gap-1 text-primary-600 visited:text-purple-700 dark:visited:text-purple-400 hover:text-primary-700"
        >
          {item.talent_name}
          <ExternalLink className="h-3 w-3" />
        </Link>
      ) : (
        <span className="text-sm text-gray-500 dark:text-gray-400">—</span>
      ),
    },
    {
      key: 'uploaded_at',
      label: 'Added',
      render: (item) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">{formatDate(item.uploaded_at)}</span>
      ),
    },
  ], [location.pathname, location.search]);

  const [details, setDetails] = useState(null);
  const [summary, setSummary] = useState(null); // кеш агрегатов (total, timeline)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  const isDark = document.documentElement.classList.contains('dark');
  const chartGridColor = isDark ? '#374151' : '#e5e7eb';
  const chartAxisColor = isDark ? '#9ca3af' : '#6b7280';
  const tooltipBg = isDark ? '#1f2937' : '#ffffff';
  const tooltipBorder = isDark ? '#374151' : '#e5e7eb';

  useEffect(() => {
    setPage(1);
    setSummary(null);
  }, [manager.manager_id]);

  useEffect(() => {
    fetchDetails();
  }, [manager.manager_id, page]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const isFirstPage = page === 1;
      const params = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        page,
        limit: 20,
        ...(isFirstPage ? {} : { page_only: true, total_hint: summary?.total_videos ?? summary?.pagination?.total })
      };

      const response = await statisticsService.getManagerYoutubeDetails(manager.manager_id, params);

      if (response.data) {
        if (isFirstPage) {
          setSummary(response.data);
        }
        setDetails(response.data);
      }
    } catch (err) {
      console.error('Error fetching youtube details:', err);
      setError(err.message || 'Failed to load details');
      toast.error('Failed to load YouTube details');
    } finally {
      setLoading(false);
    }
  };


  const formatTimelineDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <ModalPortal onClose={onClose} ariaLabel="Manager YouTube details">
      <div className="flex max-h-[calc(100dvh-1.5rem)] w-full min-w-0 max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800 sm:max-h-[calc(100dvh-4rem)]">
        {/* Header */}
        <div className="flex min-w-0 shrink-0 flex-wrap items-start justify-between gap-3 border-b border-gray-200 p-4 dark:border-gray-700 sm:p-6">
          <div className="flex min-w-0 items-center gap-3">
            <Youtube className="h-6 w-6 text-red-500" />
            <div>
              <h2 className="break-words text-2xl font-bold text-gray-900 dark:text-gray-100">
                YouTube Statistics
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {manager.manager?.name || 'Unknown Manager'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-6 w-6 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600 dark:text-red-400">
              {error}
            </div>
          ) : details ? (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <StatCard
                  title="Total YouTube & Shorts"
                  value={(summary || details).total_videos || 0}
                  icon={Youtube}
                  color="red"
                />
                <StatCard
                  title="Date Range"
                  value={`${filters.startDate} - ${filters.endDate}`}
                  icon={Calendar}
                  color="purple"
                />
              </div>

              {/* Timeline Chart */}
              {(summary || details).timeline && (summary || details).timeline.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-900 min-w-0 overflow-hidden rounded-lg p-4 sm:p-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Upload Timeline
                  </h3>
                  <ResponsiveContainer width="100%" height={300} minWidth={0}>
                    <LineChart data={(summary || details).timeline.map(t => ({
                      date: formatTimelineDate(t.date),
                      count: t.count
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: chartAxisColor, fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis tick={{ fill: chartAxisColor, fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: tooltipBg,
                          border: `1px solid ${tooltipBorder}`,
                          borderRadius: '0.5rem',
                          color: isDark ? '#f9fafb' : '#111827'
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#ef4444"
                        strokeWidth={2}
                        name="YouTube Added"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Recent Items Table */}
              <div className="min-w-0 rounded-lg bg-gray-50 p-4 dark:bg-gray-900 sm:p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
                  Recent YouTube Links
                </h3>
                <StatisticsTable
                  columns={recentYoutubeColumns}
                  data={details.recent_videos || []}
                  rowKey={youtubeItemRowKey}
                  variant="ghost"
                  emptyState={
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No recent YouTube links found
                    </div>
                  }
                  footer={details?.pagination && details.pagination.total_pages > 1 ? (
                    <Pagination
                      currentPage={page}
                      totalPages={details.pagination.total_pages}
                      totalItems={details.pagination.total}
                      itemsPerPage={20}
                      onPageChange={setPage}
                      showResults="range"
                    />
                  ) : null}
                />
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-gray-200 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] dark:border-gray-700 sm:p-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </ModalPortal>
  );
};

export default ManagerYoutubeDetailsModal;
